interface ZoneDimensions {
  width: number;
  height: number;
}

interface ZoneWithDimensions {
  id: string;
  dimensions: ZoneDimensions;
}

interface ZoneBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export interface CanvasConfig {
  minZoom: number;
  translateExtent: [[number, number], [number, number]];
  initialViewport: { x: number; y: number; zoom: number };
}

export const calculateZoneBounds = (zones: ZoneWithDimensions[], positions: { x: number; y: number }[]): ZoneBounds => {
  if (zones.length === 0) return { minX: 0, minY: 0, maxX: 1000, maxY: 1000 };

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  zones.forEach((zone, index) => {
    const position = positions[index];
    if (position) {
      const left = position.x;
      const top = position.y;
      const right = position.x + zone.dimensions.width;
      const bottom = position.y + zone.dimensions.height;

      minX = Math.min(minX, left);
      minY = Math.min(minY, top);
      maxX = Math.max(maxX, right);
      maxY = Math.max(maxY, bottom);
    }
  });

  return { minX, minY, maxX, maxY };
};

export const calculateCanvasConfig = (zones: ZoneWithDimensions[], positions: { x: number; y: number }[]): CanvasConfig => {
  // Calculate zone bounds and minimum zoom
  const zoneBounds = calculateZoneBounds(zones, positions);
  const padding = 100; // Add some padding around zones
  const topPadding = 200; // Extra buffer on top for search controls
  const canvasWidth = window.innerWidth || 1200;
  const canvasHeight = window.innerHeight || 800;
  
  const contentWidth = zoneBounds.maxX - zoneBounds.minX + (2 * padding);
  const contentHeight = zoneBounds.maxY - zoneBounds.minY + padding + topPadding;
  
  const zoomX = canvasWidth / contentWidth;
  const zoomY = canvasHeight / contentHeight;
  const calculatedMinZoom = Math.min(zoomX, zoomY, 1); // Don't go above 1x zoom
  const minZoom = Math.max(calculatedMinZoom, 0.1); // Don't go below 0.1x zoom
  
  // Calculate translate extent (panning boundaries)
  const translateExtent: [[number, number], [number, number]] = [
    [zoneBounds.minX - padding, zoneBounds.minY - topPadding], // top-left boundary with extra top buffer
    [zoneBounds.maxX + padding, zoneBounds.maxY + padding]     // bottom-right boundary
  ];
  
  // Calculate initial viewport position within bounds
  const initialX = Math.max(translateExtent[0][0], Math.min(0, translateExtent[1][0]));
  const initialY = Math.max(translateExtent[0][1], Math.min(0, translateExtent[1][1]));
  const initialZoom = Math.max(minZoom, 0.8);
  
  return {
    minZoom,
    translateExtent,
    initialViewport: { x: initialX, y: initialY, zoom: initialZoom }
  };
};